import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, CircularProgress, useTheme, Button } from '@mui/material';
import CardGrid from './components/CardGrid';
import SearchFilters from './components/SearchFilters';
import parseCards from './utils/cardParser';
import { getUrl } from './utils/app';

function App() {
  // Parse initial state from URL
  const getInitialState = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      searchTerm: params.get('q') || '',
      selectedSection: params.get('section') || '',
      filters: {
        cardType: params.get('type') || 'any',
        atkMin: params.get('atkMin') || 'any',
        atkMax: params.get('atkMax') || 'any',
        defMin: params.get('defMin') || 'any',
        defMax: params.get('defMax') || 'any',
        levelMin: params.get('levelMin') || 'any',
        levelMax: params.get('levelMax') || 'any',
        booster: params.get('booster') || 'any',
      }
    };
  };

  const [cards, setCards] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCards, setFilteredCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState([]);
  const initialState = getInitialState();
  const [selectedSection, setSelectedSection] = useState(initialState.selectedSection);
  const [filters, setFilters] = useState(initialState.filters);
  const [showScroll, setShowScroll] = useState(false);

  const theme = useTheme();

  useEffect(() => {
    const fetchCards = async () => {
      try {
        setLoading(true);
        const response = await fetch(getUrl('/cards.txt'));
        if (!response.ok) {
          throw new Error('Failed to load cards data');
        }
        const text = await response.text();
        const parsedCards = parseCards(text);
        if (parsedCards.length === 0) {
          throw new Error('No cards were found in the data');
        }
        setCards(parsedCards);
        setFilteredCards(parsedCards);
        
        // Get unique sections
        const uniqueSections = [...new Set(parsedCards.map(card => card.section))].sort();
        setSections(uniqueSections);
        
        setError(null);
      } catch (error) {
        console.error('Error loading cards:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCards();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScroll(true);
      } else {
        setShowScroll(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [filterName]: value }));
  };

  const isWithinRange = (value, min, max) => {
    if (!value) return false;
    const numericValue = parseInt(value, 10);
    const minValue = min === 'any' ? Number.MIN_SAFE_INTEGER : parseInt(min, 10);
    const maxValue = max === 'any' ? Number.MAX_SAFE_INTEGER : parseInt(max, 10);
    return numericValue >= minValue && numericValue <= maxValue;
  };

  useEffect(() => {
    let filtered = [...cards];

    // Apply section filter
    if (selectedSection) {
      filtered = filtered.filter(card => card.section === selectedSection);
    }

    // Apply search term filter
    if (searchTerm.trim()) {
      const searchTermLower = searchTerm.toLowerCase();
      filtered = filtered.filter(card => 
        (card.name?.toLowerCase().includes(searchTermLower)) ||
        (card.description?.toLowerCase().includes(searchTermLower)) ||
        (card.type?.toLowerCase().includes(searchTermLower)) ||
        (card.cardType?.toLowerCase().includes(searchTermLower))
      );
    }

    // Apply card type filter
    if (filters.cardType !== 'any') {
      filtered = filtered.filter(card => 
        card.cardType?.includes(filters.cardType)
      );
    }

    // Apply ATK filter
    if (filters.atkMin !== 'any' || filters.atkMax !== 'any') {
      filtered = filtered.filter(card => 
        card.atk && isWithinRange(card.atk, filters.atkMin, filters.atkMax)
      );
    }

    // Apply DEF filter
    if (filters.defMin !== 'any' || filters.defMax !== 'any') {
      filtered = filtered.filter(card => 
        card.def && isWithinRange(card.def, filters.defMin, filters.defMax)
      );
    }

    // Apply Level filter
    if (filters.levelMin !== 'any' || filters.levelMax !== 'any') {
      filtered = filtered.filter(card => 
        card.level && isWithinRange(card.level, filters.levelMin, filters.levelMax)
      );
    }

    // Apply booster filter
    if (filters.booster !== 'any') {
      filtered = filtered.filter(card => 
        card.booster?.includes(filters.booster)
      );
    }

    setFilteredCards(filtered);
  }, [searchTerm, selectedSection, cards, filters]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    // Only add parameters that have non-default values
    if (searchTerm) params.set('q', searchTerm);
    if (selectedSection) params.set('section', selectedSection);
    if (filters.cardType !== 'any') params.set('type', filters.cardType);
    if (filters.atkMin !== 'any') params.set('atkMin', filters.atkMin);
    if (filters.atkMax !== 'any') params.set('atkMax', filters.atkMax);
    if (filters.defMin !== 'any') params.set('defMin', filters.defMin);
    if (filters.defMax !== 'any') params.set('defMax', filters.defMax);
    if (filters.levelMin !== 'any') params.set('levelMin', filters.levelMin);
    if (filters.levelMax !== 'any') params.set('levelMax', filters.levelMax);
    if (filters.booster !== 'any') params.set('booster', filters.booster);

    // Update URL without refreshing the page
    const newUrl = params.toString() 
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }, [searchTerm, selectedSection, filters]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const newState = getInitialState();
      setSearchTerm(newState.searchTerm);
      setSelectedSection(newState.selectedSection);
      setFilters(newState.filters);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Container maxWidth="xl">
      <Box 
        sx={{ 
          textAlign: 'center', 
          my: 4,
          p: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper',
          boxShadow: '0 0 20px rgba(176, 141, 87, 0.1)',
          border: '1px solid',
          borderColor: 'rgba(176, 141, 87, 0.2)',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Yu-Gi-Oh! The Eternal Duelist Soul
        </Typography>
        <Typography variant="h5" component="h2" color="text.secondary" gutterBottom>
          Card Explorer
        </Typography>
      </Box>
      <SearchFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedSection={selectedSection}
        onSectionChange={setSelectedSection}
        sections={sections}
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" variant="h6" align="center">
          {error}
        </Typography>
      ) : (
        <CardGrid cards={filteredCards} theme={theme} />
      )}
      {showScroll && (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16 }}>
          <Button variant="contained" color="primary" onClick={scrollToTop}>
            Back to Top
          </Button>
        </Box>
      )}
    </Container>
  );
}

export default App;
